import React, { useEffect, useRef } from 'react';
import { Box, BoxProps } from '@mui/material';
import { Renderer, Program, Mesh, Color, Triangle } from 'ogl';

interface ThreadsBackgroundProps extends BoxProps {
  threadCount?: number;
  speed?: number;
  thickness?: number;
  color?: string;
  backgroundColor?: string;
  mouseInteraction?: boolean;
  transparent?: boolean;
}

const vertexShader = `
attribute vec2 uv;
attribute vec2 position;
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position, 0, 1);
}
`;

const fragmentShader = `
precision highp float;

uniform float uTime;
uniform vec3 uResolution;
uniform vec2 uMouse;
uniform float uThreadCount;
uniform float uSpeed;
uniform float uThickness;
uniform vec3 uColor;
uniform float uMouseActiveFactor;
uniform bool uTransparent;

varying vec2 vUv;

#define PI 3.14159265359

float hash(vec2 p) {
  p = fract(p * vec2(443.897, 441.423));
  p += dot(p.xy, p.yx + 19.19);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float thread(vec2 uv, float id, float time) {
  float t = id / uThreadCount;
  float offset = hash(vec2(id, 0.0)) * 10.0;
  
  // Create thread path using noise
  float x = uv.x;
  float y = uv.y;
  
  // Animated thread path
  float pathY = noise(vec2(x * 2.0 + time * uSpeed * 0.5 + offset, id)) * 0.3 - 0.15;
  pathY += sin(x * 3.0 + time * uSpeed + offset) * 0.1;
  pathY += cos(x * 5.0 + time * uSpeed * 0.7 + offset) * 0.05;
  
  // Mouse interaction
  vec2 mousePos = (uMouse - vec2(0.5)) * 2.0;
  float mouseDist = length(uv - mousePos);
  pathY += (1.0 - smoothstep(0.0, 0.3, mouseDist)) * 0.2 * uMouseActiveFactor;
  
  float dist = abs(y - (t + pathY));
  float thread = smoothstep(uThickness, 0.0, dist);
  
  // Add glow
  float glow = smoothstep(uThickness * 2.0, 0.0, dist) * 0.3;
  
  return thread + glow;
}

void main() {
  vec2 uv = vUv;
  vec2 aspect = vec2(uResolution.x / uResolution.y, 1.0);
  uv = (uv - 0.5) * aspect;
  
  vec3 col = vec3(0.0);
  
  // Draw multiple threads
  for (float i = 0.0; i < uThreadCount; i += 1.0) {
    float threadValue = thread(uv, i, uTime);
    vec3 threadColor = uColor;
    
    // Vary color slightly per thread
    float hueShift = hash(vec2(i, 1.0)) * 0.1;
    threadColor = mix(threadColor, uColor * (1.0 + hueShift), 0.3);
    
    col += threadValue * threadColor;
  }
  
  // Clamp and apply alpha
  col = min(col, 1.0);
  
  if (uTransparent) {
    float alpha = length(col);
    alpha = smoothstep(0.0, 0.5, alpha);
    gl_FragColor = vec4(col, alpha);
  } else {
    gl_FragColor = vec4(col, 1.0);
  }
}
`;

export const ThreadsBackground: React.FC<ThreadsBackgroundProps> = ({
  children,
  threadCount = 8,
  speed = 1.0,
  thickness = 0.01,
  color = '#9A8174',
  backgroundColor = 'transparent',
  mouseInteraction = true,
  transparent = true,
  sx,
  ...props
}) => {
  const ctnDom = useRef<HTMLDivElement>(null);
  const targetMousePos = useRef({ x: 0.5, y: 0.5 });
  const smoothMousePos = useRef({ x: 0.5, y: 0.5 });
  const targetMouseActive = useRef(0.0);
  const smoothMouseActive = useRef(0.0);

  // Convert hex color to RGB
  const hexToRgb = (hex: string): [number, number, number] => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [
          parseInt(result[1], 16) / 255,
          parseInt(result[2], 16) / 255,
          parseInt(result[3], 16) / 255,
        ]
      : [0.6, 0.5, 0.45]; // Default taupe color
  };

  const rgbColor = hexToRgb(color);

  useEffect(() => {
    if (!ctnDom.current) return;

    const ctn = ctnDom.current;

    const renderer = new Renderer({
      alpha: transparent,
      premultipliedAlpha: false,
    });

    const gl = renderer.gl;

    if (transparent) {
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.clearColor(0, 0, 0, 0);
    } else {
      gl.clearColor(0, 0, 0, 1);
    }

    let program: Program | null = null;

    function resize() {
      const scale = 1;
      renderer.setSize(ctn.offsetWidth * scale, ctn.offsetHeight * scale);
      if (program) {
        program.uniforms.uResolution.value = new Color(
          gl.canvas.width,
          gl.canvas.height,
          gl.canvas.width / gl.canvas.height
        );
      }
    }

    window.addEventListener('resize', resize, false);
    resize();

    const geometry = new Triangle(gl);
    program = new Program(gl, {
      vertex: vertexShader,
      fragment: fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uResolution: {
          value: new Color(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height),
        },
        uMouse: {
          value: new Float32Array([smoothMousePos.current.x, smoothMousePos.current.y]),
        },
        uThreadCount: { value: threadCount },
        uSpeed: { value: speed },
        uThickness: { value: thickness },
        uColor: { value: new Float32Array(rgbColor) },
        uMouseActiveFactor: { value: 0.0 },
        uTransparent: { value: transparent },
      },
    });

    const mesh = new Mesh(gl, { geometry, program });

    let animateId: number;

    function update(t: number) {
      animateId = requestAnimationFrame(update);

      if (program) {
        program.uniforms.uTime.value = t * 0.001;
      }

      const lerpFactor = 0.05;
      smoothMousePos.current.x += (targetMousePos.current.x - smoothMousePos.current.x) * lerpFactor;
      smoothMousePos.current.y += (targetMousePos.current.y - smoothMousePos.current.y) * lerpFactor;
      smoothMouseActive.current += (targetMouseActive.current - smoothMouseActive.current) * lerpFactor;

      if (program) {
        program.uniforms.uMouse.value[0] = smoothMousePos.current.x;
        program.uniforms.uMouse.value[1] = smoothMousePos.current.y;
        program.uniforms.uMouseActiveFactor.value = smoothMouseActive.current;
      }

      renderer.render({ scene: mesh });
    }

    animateId = requestAnimationFrame(update);
    ctn.appendChild(gl.canvas);

    function handleMouseMove(e: MouseEvent) {
      const rect = ctn.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = 1.0 - (e.clientY - rect.top) / rect.height;
      targetMousePos.current = { x, y };
      targetMouseActive.current = 1.0;
    }

    function handleMouseLeave() {
      targetMouseActive.current = 0.0;
    }

    if (mouseInteraction) {
      ctn.addEventListener('mousemove', handleMouseMove);
      ctn.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      cancelAnimationFrame(animateId);
      window.removeEventListener('resize', resize);
      if (mouseInteraction) {
        ctn.removeEventListener('mousemove', handleMouseMove);
        ctn.removeEventListener('mouseleave', handleMouseLeave);
      }
      if (ctn.contains(gl.canvas)) {
        ctn.removeChild(gl.canvas);
      }
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, [
    threadCount,
    speed,
    thickness,
    color,
    mouseInteraction,
    transparent,
    rgbColor,
  ]);

  return (
    <Box
      {...props}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor,
        ...sx,
      }}
    >
      <Box
        ref={ctnDom}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          height: '100%',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ThreadsBackground;


